import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { Card } from '../../components/Card';
import { NoAppsAvailablePage } from '../../components/NoAppsAvailablePage';
import { Page } from '../../components/Page';
import { useVisibleApps } from '../../hooks/use-visible-apps';
import { messages } from './messages';

const styles = cssMap({
	content: {
		paddingBlock: token('space.1000'),
		textAlign: 'center',
		gap: token('space.600'),
		maxWidth: '800px',
		marginInline: 'auto',
	},
	header: {
		gap: token('space.100'),
	},
});

const Content = (): JSX.Element => {
	return (
		<Flex xcss={styles.content} direction="column">
			<Flex xcss={styles.header} direction="column">
				<Heading size="xxlarge">
					<FormattedMessage {...messages.title2} />
				</Heading>
				<Text color="color.text.subtlest">
					<FormattedMessage {...messages.description2} />
				</Text>
			</Flex>

			<Flex direction="column" gap="space.150">
				<Flex justifyContent="space-between" alignItems="center">
					<Heading size="medium">
						<FormattedMessage {...messages.quickStartTitle} />
					</Heading>
				</Flex>

				<Flex direction="column" gap="space.200">
					<Flex gap="space.200">
						<Card type="labs" />
					</Flex>
					{fg('graph_explorer_in_super_app') && (
						<Flex gap="space.200">
							<Card type="explorer" />
						</Flex>
					)}
					<Flex gap="space.200">
						<Card type="assistant" />
						<Card type="visualiser" />
					</Flex>
					<Flex gap="space.200">
						{fg('twg-graphyte-gc-show-catalog') && <Card type="catalog" />}
					</Flex>
				</Flex>
			</Flex>
		</Flex>
	);
};

export const Home = (): React.JSX.Element => {
	const { apps, loading } = useVisibleApps();
	const shouldRedirectToQueryBuilder =
		!loading && apps.length === 1 && apps[0].name === 'Query Builder';

	useEffect(() => {
		if (!shouldRedirectToQueryBuilder) {
			return;
		}

		window.history.replaceState({}, '', '/query-builder');
		window.dispatchEvent(new PopStateEvent('popstate'));
	}, [shouldRedirectToQueryBuilder]);

	if (loading) {
		return <Page />;
	}

	if (shouldRedirectToQueryBuilder) {
		return <Page />;
	}

	if (!loading && apps.length === 0) {
		return <NoAppsAvailablePage />;
	}

	return (
		<Page>
			<Content />
		</Page>
	);
};
